import { GsFormIfArgValue, GsFormIfOperator } from 'libs/shared/src/components';

export function schemaDateCompare(arg: GsFormIfArgValue, value: Date | null | undefined, operator: GsFormIfOperator) {
  if (!operator) operator = '==';
  switch (operator) {
    case '==': {
      return compareEqual(arg, value);
    }
    case '!=': {
      return compareNotEqual(arg, value);
    }
    case '<': {
      return compareLessThan(arg, value);
    }
    case '>': {
      return compareGreaterThan(arg, value);
    }
    case '<=': {
      return compareLessThan(arg, value) || compareEqual(arg, value);
    }
    case '>=': {
      return compareGreaterThan(arg, value) || compareEqual(arg, value);
    }
    default: {
      return false;
    }
  }
}

function compareEqual(val0: GsFormIfArgValue, val1?: Date | null): boolean {
  if (val0 === undefined || val0 === null) return val1 === undefined || val1 === null;
  switch (typeof val0) {
    case 'string':
      return val1?.getTime() === parseDateString(val0)?.getTime();
    case 'number':
      return val1?.getTime() === new Date(val0).getTime();
    case 'boolean':
      return false; // Dates cannot be compared to booleans
    case 'object':
      if (val0 === null) return val0 == val1;
      if (Array.isArray(val0)) return val0.some((val) => schemaDateCompare(val, val1, '=='));
      if (val0 && val0.constructor.name === 'Date') {
        return val1 ? (val0 as Date).getTime() == val1.getTime() : false;
      }
      break;
    default:
      console.debug(`[SchemaService] Unsupported value type for date comparison: ${typeof val0}`);
      return val1?.getTime() == new Date(val0).getTime();
  }
  return false;
}

function compareNotEqual(val0: GsFormIfArgValue, val1?: Date | null): boolean {
  if (val0 === undefined || val0 === null) return val1 !== undefined && val1 !== null;
  switch (typeof val0) {
    case 'string':
      return val1?.getTime() != parseDateString(val0)?.getTime();
    case 'number':
      return val1?.getTime() != new Date(val0).getTime();
    case 'boolean':
      return false; // Dates cannot be compared to booleans
    case 'object':
      if (Array.isArray(val0)) return val0.some((val) => schemaDateCompare(val, val1, '!='));
      if (val0 && val0.constructor.name === 'Date') {
        return val1 ? (val0 as Date).getTime() != val1.getTime() : false;
      }
      break;
    default:
      console.debug(`[SchemaService] Unsupported value type for date comparison: ${typeof val0}`);
      return val1?.getTime() != new Date(val0).getTime();
  }
  return false;
}

function compareLessThan(val0: GsFormIfArgValue, val1?: Date | null): boolean {
  if (val0 === undefined || val0 === null) return val1 != null; // Null/undefined is less than any actual date, but not less than null itself
  switch (typeof val0) {
    case 'string':
      return val1 ? new Date(val0).getTime() < val1.getTime() : false;
    case 'number':
      return val1 ? val0 < val1.getTime() : false;
    case 'boolean':
      return false; // Dates cannot be compared to booleans
    case 'object':
      if (Array.isArray(val0)) return val0.some((val) => schemaDateCompare(val, val1, '<'));
      if (val0 && val0.constructor.name === 'Date') {
        return val1 ? (val0 as Date).getTime() < val1.getTime() : false;
      }
      break;
    default:
      console.debug(`[SchemaService] Unsupported value type for date comparison: ${typeof val0}`);
      return val1 ? new Date(val0).getTime() < val1.getTime() : false;
  }
  return false;
}

function compareGreaterThan(val0: GsFormIfArgValue, val1?: Date | null): boolean {
  if (val0 === undefined || val0 === null) return false; // Null/undefined is never greater than any date
  switch (typeof val0) {
    case 'string':
      return val1 ? new Date(val0).getTime() > val1.getTime() : false;
    case 'number':
      return val1 ? val0 > val1.getTime() : false;
    case 'boolean':
      return false; // Dates cannot be compared to booleans
    case 'object':
      if (Array.isArray(val0)) return val0.some((val) => schemaDateCompare(val, val1, '>'));
      if (val0 && val0.constructor.name === 'Date') {
        return val1 ? (val0 as Date).getTime() > val1.getTime() : false;
      }
      break;
    default:
      console.debug(`[SchemaService] Unsupported value type for date comparison: ${typeof val0}`);
      return val1 ? new Date(val0).getTime() > val1.getTime() : false;
  }
  return false;
}

/**
 * Parses a date string in ISO 8601 format and returns a Date object.
 * @param dateString The date string to parse.
 * @returns A Date object if the string is valid, otherwise undefined.
 */
function parseDateString(dateString: string): Date | undefined {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn(`[SchemaService] Invalid date string for comparison: ${dateString}`);
    return;
  }
  return date;
}
